import { SmartCardRow } from "@/domains/tanstack-db";
import { CollectionListSmartCardRenderer } from "@/components/heads-up/cards/collection-list-smart-card-renderer";
import { EventTimelineSmartCardRenderer } from "@/components/heads-up/cards/event-timeline-smart-card-renderer";
import { MarkdownSmartCardRenderer } from "@/components/heads-up/cards/markdown-smart-card-renderer";
import { MentionedByNotesListSmartCardRenderer } from "@/components/heads-up/cards/mentioned-by-notes-list-smart-card-renderer";
import { NoteListSmartCardRenderer } from "@/components/heads-up/cards/note-list-smart-card-renderer";
import type { CollectionListSmartCardCollectionInfo } from "./collection-list-smart-card.types";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import type { Maybe } from "@/domains/common/types";
import type { HeadsUpCardEventMeta } from "@/components/heads-up/analytics";
import { HeadsUpItemSource } from "@/components/heads-up/analytics";
import { snakeCase, get, isString } from "lodash-es";
import {
  SmartCard,
  SmartCardKindValues,
  SmartCardKind as SmartCardKindEnum,
  NoteListSmartCard,
  MentionedByNotesListSmartCard,
  CollectionListSmartCard,
  EventTimelineSmartCard,
  MarkdownSmartCard,
} from "@/components/heads-up/heads-up.types";

export function toTypedSmartCard(row: SmartCardRow): SmartCard | null {
  const kind = row.derived_kind as SmartCardKindValues | null;
  const value = row.value_json as Record<string, unknown> | null;
  if (!kind || !value) return null;

  switch (kind) {
    case SmartCardKindEnum.NoteList: {
      const typed: NoteListSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.NoteList,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as NoteListSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.MentionedByNotesList: {
      const typed: MentionedByNotesListSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.MentionedByNotesList,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as MentionedByNotesListSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.CollectionList: {
      const typed: CollectionListSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.CollectionList,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as CollectionListSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.EventTimeline: {
      const typed: EventTimelineSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.EventTimeline,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as EventTimelineSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.Markdown: {
      const typed: MarkdownSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.Markdown,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          markdown: (value.markdown as string | null) ?? null,
        },
      };
      return typed;
    }
    default:
      return null;
  }
}

export interface HeadsUpCardItemMeta {
  itemKind: string;
  itemId: string;
  itemTitle?: string | null;
  itemPositionIndex: number;
  itemSource?: HeadsUpItemSource;
}

export interface HeadsUpCardRendererAnalytics {
  onItemPreviewOpen?: (meta: HeadsUpCardItemMeta) => void;
  onItemClick?: (meta: HeadsUpCardItemMeta) => void;
}

export interface HeadsUpCardAnalyticsHandlers {
  sectionPositionIndex: number;
  onItemPreviewOpen?: (meta: HeadsUpCardEventMeta) => void;
  onItemClick?: (meta: HeadsUpCardEventMeta) => void;
}

export interface RenderSmartCardOptions {
  isHoverable?: boolean;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  getCollectionInfo?: (collectionId: string) => Maybe<CollectionListSmartCardCollectionInfo>;
  analytics?: HeadsUpCardAnalyticsHandlers;
}

export function renderSmartCard(
  card: SmartCardRow,
  options: RenderSmartCardOptions = {}
): JSX.Element | null {
  const typed = toTypedSmartCard(card);
  if (!typed) return null;

  const analyticsBindings = buildHeadsUpEventBindings({ card: typed, handlers: options.analytics });

  switch (typed.kind) {
    case SmartCardKindEnum.NoteList:
      return (
        <NoteListSmartCardRenderer
          card={typed}
          getNotePreviewInfo={options.getNotePreviewInfo}
          analytics={analyticsBindings}
        />
      );
    case SmartCardKindEnum.MentionedByNotesList:
      return (
        <MentionedByNotesListSmartCardRenderer
          card={typed}
          getNotePreviewInfo={options.getNotePreviewInfo}
          analytics={analyticsBindings}
        />
      );
    case SmartCardKindEnum.CollectionList:
      return (
        <CollectionListSmartCardRenderer
          card={typed}
          getCollectionInfo={options.getCollectionInfo}
          analytics={analyticsBindings}
        />
      );
    case SmartCardKindEnum.EventTimeline:
      return (
        <EventTimelineSmartCardRenderer
          card={typed}
          getNotePreviewInfo={options.getNotePreviewInfo}
          analytics={analyticsBindings}
        />
      );
    case SmartCardKindEnum.Markdown:
      return <MarkdownSmartCardRenderer card={typed} />;
    default:
      return null;
  }
}

function buildHeadsUpEventBindings({
  card,
  handlers,
}: {
  card: SmartCard;
  handlers?: HeadsUpCardAnalyticsHandlers;
}): HeadsUpCardRendererAnalytics | undefined {
  if (!handlers) return undefined;

  const sectionKind = mapCardKindToSectionKind({ kind: card.kind });
  const sectionTitle = getCardTitle({ card });
  const sectionIsFindMore = Boolean(card.originated_from_find_more);
  const itemSource = sectionIsFindMore ? HeadsUpItemSource.FindMore : HeadsUpItemSource.Standard;

  const buildPayload = (meta: HeadsUpCardItemMeta): HeadsUpCardEventMeta => ({
    section_kind: sectionKind,
    section_title: sectionTitle,
    section_card_id: card.id,
    section_position_index: handlers.sectionPositionIndex,
    section_is_find_more: sectionIsFindMore,
    item_kind: snakeCase(meta.itemKind),
    item_id: meta.itemId,
    item_title: meta.itemTitle ?? null,
    item_position_index: meta.itemPositionIndex,
    item_source: meta.itemSource ?? itemSource,
  });

  return {
    onItemPreviewOpen: handlers.onItemPreviewOpen
      ? meta => handlers.onItemPreviewOpen?.(buildPayload(meta))
      : undefined,
    onItemClick: handlers.onItemClick
      ? meta => handlers.onItemClick?.(buildPayload(meta))
      : undefined,
  };
}

function mapCardKindToSectionKind({ kind }: { kind: SmartCardKindEnum }): string {
  return snakeCase(kind).toLowerCase();
}

function getCardTitle({ card }: { card: SmartCard }): string | null {
  const title = get(card, "value.title");

  if (!isString(title)) return null;

  const trimmed = title.trim();
  return trimmed.length > 0 ? trimmed : null;
}
